/**
 * Shared social provider brand marks + the provider list used by the native auth
 * modal (login + signup) and the account "connected accounts" screen — one
 * source of truth so the icons/labels stay consistent everywhere.
 *
 * `id` is the Keycloak IdP alias passed as kc_idp_hint.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

export const GoogleIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.5 6.2 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.5 6.2 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.4 26.7 36 24 36c-5.3 0-9.7-2.6-11.3-6.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2C39.9 35.9 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

export const FacebookIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#1877F2" d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.47h-2.796v8.384C19.612 22.954 24 17.99 24 12z" />
  </svg>
);

export const LinkedInIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#0A66C2" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
  </svg>
);

export interface SocialProvider {
  /** Keycloak IdP alias (kc_idp_hint). */
  id: string;
  /** Brand name. Never translated — Google is Google in every language. */
  short: string;
  Icon: React.FC<{ size?: number }>;
}

export const SOCIAL_PROVIDERS: SocialProvider[] = [
  { id: 'facebook', short: 'Facebook', Icon: FacebookIcon },
  { id: 'google', short: 'Google', Icon: GoogleIcon },
  { id: 'linkedin', short: 'LinkedIn', Icon: LinkedInIcon },
];

/**
 * The "OR" divider + a stacked grid of consistent, brand-iconed social buttons.
 * Reused by the login and signup views.
 *
 * Rendered as real <a> links (not JS window.location redirects): some mobile
 * privacy/anti-tracking features block *scripted* navigations to Facebook while
 * letting a genuine link click through. `hrefFor(id)` builds the auth-login URL.
 */
/**
 * Facebook web-OAuth is unreliable in mobile browsers on Android: the OS routes
 * the facebook.com hop into the Facebook app via its intent filters, and the app
 * never returns to the web flow. Hide the Facebook button there — Google and
 * LinkedIn are unaffected, and desktop keeps all three.
 *
 * Three signals, OR'd, because no single one covers every case:
 *
 *   1. UA Client Hints (`userAgentData.platform`) — structured and not derived
 *      from the legacy UA string. Absent on Safari/Firefox, hence the fallbacks.
 *   2. The classic `Android` UA token — covers every real Android browser,
 *      including in-app WebViews.
 *   3. A Linux UA that reports touch points. This is the case signal 2 misses:
 *      Chrome's "Request desktop site" on Android swaps in a desktop
 *      `X11; Linux x86_64` UA with no Android token — but the OS intent filter
 *      is app-level, not UA-level, so Facebook still gets hijacked. A genuine
 *      Linux desktop reports maxTouchPoints === 0, so this separates the two.
 *      A touchscreen Linux laptop is a harmless false positive (it just loses
 *      one of three sign-in buttons).
 *
 * This is a client-side nicety, not a security control — it only decides whether
 * we render a button.
 */
const isAndroid = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  if (uaData?.platform === 'Android') return true;

  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return true;

  return /Linux/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 0;
};

export const SocialButtons: React.FC<{ hrefFor: (id: string) => string; disabled?: boolean }> = ({ hrefFor, disabled }) => {
  const { t } = useTranslation('auth');
  const providers = SOCIAL_PROVIDERS.filter((p) => !(p.id === 'facebook' && isAndroid()));
  return (
    <>
      <div className="d-flex align-items-center gap-2 my-3 text-muted small">
        <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} /> {t('social.or')}
        <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
      </div>
      <div className="d-grid gap-2">
        {providers.map(({ id, short, Icon }) => (
          <a
            key={id}
            href={disabled ? undefined : hrefFor(id)}
            className={`btn btn-outline-secondary d-flex align-items-center justify-content-center gap-2${disabled ? ' disabled' : ''}`}
            aria-disabled={disabled || undefined}
          >
            {/* One key with the brand interpolated: "Continue with X" is a
                single sentence a translator should own, and the brand name
                itself is never translated. */}
            <Icon size={18} /> {t('social.continueWith', { provider: short })}
          </a>
        ))}
      </div>
    </>
  );
};
